import React, { useEffect, useRef, useState } from 'react';
import { Users, Building2, MapPin, Briefcase, Shield } from 'lucide-react';

const TrustStats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({ workers: 0, industries: 0, cities: 0 });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const targets = { workers: 200, industries: 5, cities: 10 };
    const duration = 1500;
    const steps = 40;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setCounts({
        workers: Math.round(targets.workers * progress),
        industries: Math.round(targets.industries * progress),
        cities: Math.round(targets.cities * progress),
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [isVisible]);

  const stats = [
    { icon: Users, value: `${counts.workers}+`, label: 'Workers Placed', desc: 'Across British Columbia' },
    { icon: Building2, value: counts.industries, label: 'Industries Served', desc: 'Specialized Sectors' },
    { icon: MapPin, value: `${counts.cities}+`, label: 'Cities Covered', desc: 'Province-Wide Reach' },
    { icon: Shield, value: '100%', label: 'WCB Compliant', desc: 'WorkSafe BC Certified' },
    { icon: Briefcase, value: '24-48h', label: 'Response Time', desc: 'Fast Worker Placement' },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#003366] py-10 px-6 overflow-hidden"
      data-testid="trust-stats-section"
      aria-label="Company statistics"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-[#002244] via-[#003366] to-[#002244] opacity-50"></div>
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`text-center transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              data-testid={`stat-${index}`}
            >
              <stat.icon className="mx-auto mb-2 text-blue-300" size={28} aria-hidden="true" />
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-sm font-semibold text-blue-200 uppercase tracking-wide">{stat.label}</div>
              <div className="text-xs text-blue-300 mt-1">{stat.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustStats;
